<template>
  <transition name="ts-fade">
    <div
      v-if="enabled && suggestions.length && focusedEl"
      class="ts-pop"
      :style="popStyle"
      role="listbox"
      :aria-label="$t('Components.TypingSuggestions.ariaLabel')"
      @mousedown.prevent
    >
      <button
        v-for="(word, i) in suggestions"
        :key="i"
        class="ts-pop__chip"
        @mousedown.prevent="accept(word)"
        @click.prevent="accept(word)"
        type="button"
        role="option"
      >
        {{ word }}
      </button>
    </div>
  </transition>
</template>

<script>
// Lightweight word-completion helper: a built-in dictionary of the
// most common English words, plus any words the user has typed in
// the current session. Suggestions appear above the focused text
// field and accept on click (or can be wired to Tab).

const COMMON_WORDS = [
  'the',
  'and',
  'you',
  'that',
  'with',
  'this',
  'have',
  'from',
  'they',
  'for',
  'are',
  'was',
  'not',
  'been',
  'your',
  'what',
  'when',
  'where',
  'which',
  'would',
  'could',
  'should',
  'there',
  'their',
  'about',
  'because',
  'really',
  'thanks',
  'thank',
  'hello',
  'please',
  'welcome',
  'event',
  'session',
  'speaker',
  'sponsor',
  'schedule',
  'program',
  'register',
  'sign',
  'login',
  'logout',
  'profile',
  'settings',
  'notification',
  'chat',
  'message',
  'meeting',
  'agenda',
  'calendar',
  'today',
  'tomorrow',
  'morning',
  'evening',
  'looking',
  'forward',
  'meeting',
  'presentation',
  'workshop',
  'keynote',
  'panel',
  'discussion',
  'question',
  'answer',
  'break',
  'lunch',
  'dinner',
  'networking',
  'community',
  'welcome',
  'accessibility',
  'keyboard',
  'screen',
  'cursor',
  'contrast',
  'saturation',
  'spacing',
  'height',
  'align',
  'dyslexia',
  'tooltips',
  'animations'
];

export default {
  name: 'TypingSuggestions',
  data() {
    return {
      focusedEl: null,
      value: '',
      history: new Set(COMMON_WORDS),
      popStyle: {}
    };
  },
  computed: {
    enabled() {
      return !!this.$store.getters['Accessibility/getFeatures']
        .typingSuggestions;
    },
    currentPrefix() {
      if (!this.focusedEl) return '';
      const val = this.value || '';
      const m = val.match(/([A-Za-z]{1,})$/);
      return m && m[1] ? m[1].toLowerCase() : '';
    },
    suggestions() {
      const prefix = this.currentPrefix;
      if (!prefix || prefix.length < 2) return [];
      const list = Array.from(this.history);
      const filtered = list
        .filter(
          (w) => w.length > prefix.length && w.toLowerCase().startsWith(prefix)
        )
        .slice(0, 4);
      return filtered;
    }
  },
  watch: {
    enabled(v) {
      if (v) this.bind();
      else this.unbind();
    }
  },
  mounted() {
    if (this.enabled) this.bind();
  },
  beforeDestroy() {
    this.unbind();
  },
  methods: {
    bind() {
      if (this._bound) return;
      this._focusHandler = (e) => this.onFocusIn(e);
      this._inputHandler = (e) => this.onInput(e);
      this._clickHandler = (e) => this.onDocClick(e);
      this._scrollHandler = () => this.positionPop();
      document.addEventListener('focusin', this._focusHandler, true);
      document.addEventListener('input', this._inputHandler, true);
      document.addEventListener('mousedown', this._clickHandler, true);
      window.addEventListener('scroll', this._scrollHandler, true);
      window.addEventListener('resize', this._scrollHandler);
      this._bound = true;
    },
    unbind() {
      if (!this._bound) return;
      document.removeEventListener('focusin', this._focusHandler, true);
      document.removeEventListener('input', this._inputHandler, true);
      document.removeEventListener('mousedown', this._clickHandler, true);
      window.removeEventListener('scroll', this._scrollHandler, true);
      window.removeEventListener('resize', this._scrollHandler);
      this.focusedEl = null;
      this._bound = false;
    },
    onDocClick(e) {
      // Hide the popup if the user clicks away from both the input
      // and the suggestion popup. We listen on mousedown (capture)
      // so we don't race the chip's click handler.
      if (!this.focusedEl) return;
      const t = e.target;
      if (!t) return;
      if (this.focusedEl === t || this.focusedEl.contains(t)) return;
      if (t.closest && t.closest('.ts-pop')) return;
      this.focusedEl = null;
    },
    isCandidate(el) {
      if (!el || !el.tagName) return false;
      if (
        el.tagName === 'INPUT' &&
        (el.type === 'text' ||
          el.type === 'search' ||
          el.type === 'email' ||
          !el.type)
      )
        return true;
      if (el.tagName === 'TEXTAREA') return true;
      if (el.isContentEditable) return true;
      return false;
    },
    onFocusIn(e) {
      if (this.isCandidate(e.target)) {
        this.focusedEl = e.target;
        this.value = e.target.value || e.target.textContent || '';
        this.positionPop();
      } else if (!e.target.closest('.ts-pop')) {
        this.focusedEl = null;
      }
    },
    onInput(e) {
      if (e.target !== this.focusedEl) return;
      this.value = e.target.value || e.target.textContent || '';
      this.positionPop();
      this.rememberWords(this.value);
    },
    rememberWords(text) {
      // Add long-enough words from the current text to the history so
      // the user's own vocabulary starts suggesting after a while.
      const words = (text.match(/[A-Za-z]{4,}/g) || []).map((w) =>
        w.toLowerCase()
      );
      words.forEach((w) => this.history.add(w));
    },
    positionPop() {
      if (!this.focusedEl) return;
      this.$nextTick(() => {
        const r = this.focusedEl.getBoundingClientRect();
        this.popStyle = {
          left: r.left + 'px',
          top: r.top - 48 + 'px',
          maxWidth: Math.max(240, r.width) + 'px'
        };
      });
    },
    accept(word) {
      const el = this.focusedEl;
      if (!el || !el.isConnected) return;
      const prefix = this.currentPrefix || '';
      // Refocus the input so the selection APIs work even if a prior
      // mousedown transferred focus elsewhere for a frame.
      el.focus();
      if (el.isContentEditable) {
        document.execCommand(
          'insertText',
          false,
          word.slice(prefix.length) + ' '
        );
      } else {
        const caretEnd = el.selectionEnd ?? (el.value ? el.value.length : 0);
        const caretStart = caretEnd - prefix.length;
        const insertion = word + ' ';
        const before = el.value.slice(0, caretStart);
        const after = el.value.slice(caretEnd);
        el.value = before + insertion + after;
        const newPos = caretStart + insertion.length;
        el.selectionStart = el.selectionEnd = newPos;
        el.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.value = el.value != null ? el.value : el.textContent || '';
      this.rememberWords(word);
      // Keep the popup hidden until the user types the next letter
      this.$nextTick(() => this.positionPop());
    }
  }
};
</script>

<style scoped lang="scss">
.ts-pop {
  position: fixed;
  z-index: 2147483643; /* above any modal backdrop */
  display: flex;
  gap: 6px;
  background: #111827;
  border-radius: 10px;
  padding: 6px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  font-family: 'Poppins', sans-serif;
  overflow: hidden;
  max-width: 360px;

  &__chip {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #ffffff;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 12px;
    border-radius: 9999px;
    cursor: pointer;
    transition: background 0.12s;
    white-space: nowrap;

    &:hover {
      background: var(--color-primary, #047873);
      border-color: var(--color-primary, #047873);
    }
  }
}

.ts-fade-enter-active,
.ts-fade-leave-active {
  transition: opacity 0.15s;
}
.ts-fade-enter,
.ts-fade-leave-to {
  opacity: 0;
}
</style>
